/**
 * Frosted glass: two-pass separable gaussian blur + tint composite.
 *
 * Pass 1 (horizontal): sample 9 taps along u, weighted gaussian, write to ping framebuffer.
 * Pass 2 (vertical):   sample 9 taps along v from ping, weighted gaussian, write to default fb.
 * Pass 3 (tint):       sample blurred image and multiply by tint color (alpha blends).
 *
 * To keep boilerplate small we encode pass-1 and pass-2 with a uniform `uDir`
 * (vec2(1,0) or vec2(0,1)) so a single program handles both directions. Pass 3
 * is the same program with uDir=0 and a uTint multiplier path — but to keep
 * fragment-shader branching out we use a separate tiny tint program.
 */
export const frostedGlassVertexShader = /* glsl */ `
attribute vec2 a_position;
attribute vec2 a_texCoord;
varying vec2 v_uv;
void main() {
  v_uv = a_texCoord;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

/**
 * Separable gaussian (9 taps). Weights are a discrete approximation of a
 * gaussian with sigma ≈ radius/3. Caller controls effective blur strength via
 * uRadius (in CSS pixels), which the shader divides by uResolution to get UV
 * step size.
 */
export const frostedGlassBlurFragmentShader = /* glsl */ `
precision mediump float;
uniform sampler2D uTexture;
uniform vec2 uResolution;
uniform vec2 uDir;        // (1,0) horizontal pass, (0,1) vertical pass
uniform float uRadius;    // CSS px
varying vec2 v_uv;

void main() {
  // 9-tap gaussian, normalized
  float w0 = 0.2270270270;
  float w1 = 0.1945945946;
  float w2 = 0.1216216216;
  float w3 = 0.0540540541;
  float w4 = 0.0162162162;

  vec2 step = (uDir * uRadius) / uResolution;
  vec4 sum = vec4(0.0);
  sum += texture2D(uTexture, v_uv) * w0;
  sum += texture2D(uTexture, v_uv + step * 1.0) * w1;
  sum += texture2D(uTexture, v_uv - step * 1.0) * w1;
  sum += texture2D(uTexture, v_uv + step * 2.0) * w2;
  sum += texture2D(uTexture, v_uv - step * 2.0) * w2;
  sum += texture2D(uTexture, v_uv + step * 3.0) * w3;
  sum += texture2D(uTexture, v_uv - step * 3.0) * w3;
  sum += texture2D(uTexture, v_uv + step * 4.0) * w4;
  sum += texture2D(uTexture, v_uv - step * 4.0) * w4;
  gl_FragColor = sum;
}
`;

/**
 * Tint multiply: outputs blurred color with tint blended on top.
 * uTint is (r, g, b, a) in 0..1.
 */
export const frostedGlassTintFragmentShader = /* glsl */ `
precision mediump float;
uniform sampler2D uTexture;
uniform vec4 uTint;
varying vec2 v_uv;
void main() {
  vec4 base = texture2D(uTexture, v_uv);
  vec3 outColor = mix(base.rgb, uTint.rgb, uTint.a);
  gl_FragColor = vec4(outColor, base.a);
}
`;
